import type { Decoder } from './decode.ts'
import {
  array,
  decode,
  DecodeError,
  nonEmptyStr,
  nonNegInt,
  object,
  positiveInt,
} from './decode.ts'

/**
 * The data of each kind of free-spins feature, as a feature update carries
 * it (`FeatureUpdate.state.data`). The server decides it at the trigger;
 * the cabinet shows it. Both sides check it with the decoder of its kind
 * (`featureDataDecoders`), so the engine's feature rules and the client's
 * response decoders agree on it.
 */

// -----------------------------------------------------------------------------
// freeSpinsExpanding: Book of Ra's expanding symbol
// -----------------------------------------------------------------------------

/**
 * Book of Ra's feature: one symbol, picked at the trigger, expands on every
 * free game and pays on all played lines.
 */
export const FEATURE_KIND_EXPANDING = 'freeSpinsExpanding'

export interface ExpandingSymbolData {
  expandingSymbolId: string
  /**
   * The symbol's pay per PLAYED LINE at the locked stake, one entry per
   * count it pays, in ascending count. An expanding win on `count` reels
   * pays `perLine` × the played lines.
   */
  paysPerLine: { count: number; perLine: number }[]
}

const pays = array(object({ count: positiveInt, perLine: nonNegInt }))

const paysPerLine: Decoder<ExpandingSymbolData['paysPerLine']> = (
  value,
  path,
) => {
  const decoded = pays(value, path)
  if (decoded.length === 0) {
    throw new DecodeError(path, 'at least one pay')
  }
  decoded.forEach((pay, index) => {
    if (index > 0 && pay.count <= decoded[index - 1].count) {
      throw new DecodeError(
        `${path}[${index}].count`,
        'a count above the one before',
      )
    }
  })
  return decoded
}

const expandingSymbolData = object<ExpandingSymbolData>({
  expandingSymbolId: nonEmptyStr,
  paysPerLine,
})

/** @throws DecodeError when `value` is not valid expanding-symbol data. */
export function decodeExpandingSymbolData(value: unknown): ExpandingSymbolData {
  return decode(expandingSymbolData, value)
}

// -----------------------------------------------------------------------------
// freeSpinsMultiplier: every win of a free game is multiplied
// -----------------------------------------------------------------------------

/**
 * The Money Game's feature: every line and scatter win of a free game is
 * multiplied by a fixed number. Nothing is picked at the trigger and no
 * draw data is involved: the data is the multiplier the server applies,
 * which the cabinet may show (the amounts it receives already include it).
 */
export const FEATURE_KIND_MULTIPLIER = 'freeSpinsMultiplier'

/** The lowest and the highest multiplier a multiplier feature may set. */
export const MIN_WIN_MULTIPLIER = 2
export const MAX_WIN_MULTIPLIER = 10

export interface MultiplierFeatureData {
  /** A whole number from `MIN_WIN_MULTIPLIER` to `MAX_WIN_MULTIPLIER`, fixed for the feature. */
  winMultiplier: number
}

const winMultiplier: Decoder<number> = (value, path) => {
  if (
    !Number.isSafeInteger(value) ||
    (value as number) < MIN_WIN_MULTIPLIER ||
    (value as number) > MAX_WIN_MULTIPLIER
  ) {
    throw new DecodeError(
      path,
      `a whole number from ${MIN_WIN_MULTIPLIER} to ${MAX_WIN_MULTIPLIER}`,
    )
  }
  return value as number
}

const multiplierFeatureData = object<MultiplierFeatureData>({ winMultiplier })

/** @throws DecodeError when `value` is not valid multiplier feature data. */
export function decodeMultiplierFeatureData(
  value: unknown,
): MultiplierFeatureData {
  return decode(multiplierFeatureData, value)
}

// -----------------------------------------------------------------------------
// Registry
// -----------------------------------------------------------------------------

/**
 * The data decoder of every feature kind a config can name, by
 * `featureKind`. Each checks data from outside the process and throws a
 * DecodeError rooted at `$`.
 */
export const featureDataDecoders = {
  [FEATURE_KIND_EXPANDING]: decodeExpandingSymbolData,
  [FEATURE_KIND_MULTIPLIER]: decodeMultiplierFeatureData,
} as const satisfies Readonly<Record<string, (value: unknown) => unknown>>

/** A registered feature kind: a key of `featureDataDecoders`. */
export type FeatureKind = keyof typeof featureDataDecoders

/** Whether `value` names a registered feature kind (own keys only: `'toString'` is none). */
export function isFeatureKind(value: string): value is FeatureKind {
  return Object.hasOwn(featureDataDecoders, value)
}
